import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type VatSummaryRateRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import { pickDefaultOrg } from '@/store/defaults';

async function downloadXml(orgId: string, year: number, month: number) {
  const blob = await reports.vatDeclarationXml({
    organization_id: orgId,
    period_year: year,
    period_month: month,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `J0200124_${year}_${String(month).padStart(2, '0')}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];

function RateTable({ title, rows, total }: {
  title: string;
  rows: VatSummaryRateRow[];
  total: string;
}) {
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 130 }}>Ставка</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>База без ПДВ</th>
            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Сума ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                Немає записів
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.rate_code} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '0.5rem' }}>{r.rate_label}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(r.base)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(r.vat)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid var(--border-strong)', fontWeight: 600 }}>
            <td style={{ padding: '0.5rem' }}>Усього ПДВ</td>
            <td style={{ padding: '0.5rem' }}></td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function VatSummaryPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const now = new Date();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(2);

  if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);

  const data = useQuery({
    queryKey: ['vat-summary', orgId, year, month],
    queryFn: () => reports.vatSummary({
      organization_id: orgId,
      period_year: year,
      period_month: month,
    }),
    enabled: !!orgId,
  });

  const d = data.data;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Зведений звіт з ПДВ (декларація)</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => data.refetch()}>Оновити</button>
          <button
            disabled={!orgId}
            onClick={() => navigate(
              `/reports/vat-declaration-print?organization_id=${orgId}&period_year=${year}&period_month=${month}`,
            )}
          >
            🖨 Друкована форма
          </button>
          <button
            disabled={!orgId}
            onClick={() => void downloadXml(orgId, year, month)}
            title="Чорновий XML декларації — потребує доопрацювання під актуальну XSD ДПС"
          >
            ⬇ XML декларації (чорновик)
          </button>
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Рік</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())}
            min={2020}
            max={2100}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Місяць</label>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
            {MONTHS_UK.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {d && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            marginTop: '1rem',
          }}>
            <RateTable
              title="Податкові зобов'язання (постачання)"
              rows={d.obligations}
              total={d.total_obligations}
            />
            <RateTable
              title="Податковий кредит (придбання)"
              rows={d.credit}
              total={d.total_credit}
            />
          </div>

          <div style={{
            marginTop: '1.5rem',
            padding: '1.25rem',
            background: 'var(--bg-soft-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1rem',
            textAlign: 'center',
          }}>
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Зобов'язання</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{fmt(d.total_obligations)}</div>
            </div>
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Кредит</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 600 }}>{fmt(d.total_credit)}</div>
            </div>
            <div>
              {Number(d.to_pay) > 0 ? (
                <>
                  <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.85rem' }}>До сплати</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--badge-red-fg)' }}>
                    {fmt(d.to_pay)}
                  </div>
                </>
              ) : Number(d.to_refund) > 0 ? (
                <>
                  <div style={{ color: 'var(--badge-green-fg)', fontSize: '0.85rem' }}>До відшкодування</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--badge-green-fg)' }}>
                    {fmt(d.to_refund)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Підсумок</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>0,00</div>
                </>
              )}
            </div>
          </div>

          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem',
            background: 'var(--badge-amber-bg)', border: '1px solid #fbbf24',
            borderRadius: 6, fontSize: '0.85rem', color: 'var(--badge-amber-fg)',
          }}>
            <strong>Чорновий XML декларації</strong>: кнопка вище згенерує
            файл у структурі, наближеній до форми Ф0200124 ДПС.
            Перед поданням потрібно: (1) звірити з актуальною XSD-схемою
            ДПС; (2) перенести/перевірити дані у кабінеті платника
            податків ДПС; (3) накласти КЕП. Без КЕП документ не приймається.
          </div>
        </>
      )}
    </div>
  );
}
